import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { extractErrorDetail } from '@/utils/errorHandler';
import {
  createCollection as createCollectionApi,
  createCollectionAi,
  deleteCollection as deleteCollectionApi,
  deletePromptFromPromptCollection,
  getPromptCollectionPrompts,
  searchPromptCollection,
  updateCollection as updateCollectionApi,
  updatePromptFromPromptCollection,
  addPromptToPromptCollection,
  type PromptCollectionCreate,
  type PromptCollectionInDb,
  type PromptCollectionUpdate,
  type PromptCreate,
  type PromptInDb,
  type PromptUpdate,
  type SearchQuery,
} from '@/api';
import { logger } from '@/utils/logger';

export const usePromptCollectionsStore = defineStore('promptCollections', () => {
  // 1. State
  const collections = ref<PromptCollectionInDb[]>([]);
  /** Total matching the last fetch's query — drives the list's pagination. */
  const totalCount = ref<number>(0);
  const currentCollection = ref<PromptCollectionInDb | null>(null);
  const currentPrompts = ref<PromptInDb[]>([]);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // 2. Getters
  const getCollection = computed(() => (id: string): PromptCollectionInDb | undefined => {
    return collections.value.find((c: PromptCollectionInDb) => c.uid === id);
  });

  // 3. Actions
  // Fetch all collections
  async function fetchCollections(
    query: {
      search_term?: string;
      page?: number;
      page_size?: number;
      sort_key?: string;
      sort_direction?: 'asc' | 'desc';
    } = {
      search_term: '',
      page: 1,
      page_size: 50,
    }
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Create a proper SearchQuery from the query params
      const searchQuery: SearchQuery = {
        text: query.search_term ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.page_size || 50) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.page_size || 50,
        sort_key: query.sort_key,
        sort_direction: query.sort_direction,
      };

      const response = await searchPromptCollection({ body: searchQuery });
      if (!response.data) {
        throw new Error('Failed to fetch prompt collections');
      }
      const result = response.data;
      collections.value = result.results;
      totalCount.value = result.result_count;
    } catch (error_) {
      error.value = extractErrorDetail(error_, 'Failed to fetch prompt collections');
      logger.error('Error fetching prompt collections:', error_);
    } finally {
      isLoading.value = false;
    }
  }

  // Create a new collection
  async function createCollection(name: string): Promise<PromptCollectionInDb | null> {
    isLoading.value = true;
    error.value = null;

    try {
      const newCollection: PromptCollectionCreate = {
        name,
      };

      const response = await createCollectionApi({ body: newCollection });
      if (!response.data) {
        throw new Error('Failed to create prompt collection');
      }
      const result = response.data;
      collections.value.push(result);
      return result;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error creating prompt collection:', error_);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  // Delete a collection
  async function deleteCollection(id: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      await deleteCollectionApi({ path: { prompt_collection_uid: id } });

      collections.value = collections.value.filter((c: PromptCollectionInDb) => c.uid !== id);

      if (currentCollection.value?.uid === id) {
        currentCollection.value = null;
        currentPrompts.value = [];
      }

      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error deleting prompt collection:', error_);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Update a collection
  async function updateCollection(id: string, updates: PromptCollectionUpdate): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const apiResponse = await updateCollectionApi({
        path: { prompt_collection_uid: id },
        body: updates,
      });
      if (apiResponse.error) {
        throw new Error(
          extractErrorDetail(apiResponse.error, 'Failed to update prompt collection')
        );
      }
      const updated = apiResponse.data;

      // Update in collections list
      const index = collections.value.findIndex((c: PromptCollectionInDb) => c.uid === id);
      if (index !== -1) {
        collections.value[index] = updated;
      }

      // Update current collection if it's the one being edited
      if (currentCollection.value?.uid === id) {
        currentCollection.value = updated;
      }

      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error updating prompt collection:', error_);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Fetch single collection with its prompts
  async function fetchCollectionWithPrompts(id: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Find collection in existing data first
      let existingCollection = collections.value.find((c: PromptCollectionInDb) => c.uid === id);

      // If not found in memory, we need to fetch it explicitly
      if (!existingCollection) {
        try {
          // First, fetch all collections if we haven't already
          if (collections.value.length === 0) {
            await fetchCollections();
            // Check again if our collection is now loaded
            existingCollection = collections.value.find((c: PromptCollectionInDb) => c.uid === id);
          }
        } catch (error_) {
          logger.error('Error fetching collections:', error_);
        }
      }

      // Set the current collection
      if (existingCollection) {
        currentCollection.value = existingCollection;
      } else {
        // If still not found, set to null to trigger appropriate UI feedback
        currentCollection.value = null;
      }

      // Fetch the prompts for this collection
      const response = await getPromptCollectionPrompts({ path: { prompt_collection_uid: id } });
      if (!response.data) {
        throw new Error('Failed to fetch prompts for collection');
      }
      currentPrompts.value = response.data;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error fetching collection details:', error_);
      currentCollection.value = null;
      currentPrompts.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  // Add a prompt to a collection
  async function addPrompt(collectionId: string, prompt: PromptCreate): Promise<PromptInDb | null> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await addPromptToPromptCollection({
        path: { prompt_collection_uid: collectionId },
        body: prompt,
      });

      if (!response.data) {
        throw new Error('Failed to add prompt to collection');
      }
      const newPrompt = response.data;
      currentPrompts.value.push(newPrompt);
      return newPrompt;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error adding prompt:', error_);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  // Update a prompt in a collection
  async function updatePrompt(
    collectionId: string,
    promptId: string,
    updates: PromptUpdate
  ): Promise<PromptInDb | null> {
    isLoading.value = true;
    error.value = null;

    try {
      const apiResponse = await updatePromptFromPromptCollection({
        path: {
          prompt_collection_uid: collectionId,
          prompt_uid: promptId,
        },
        body: updates,
      });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to update prompt'));
      }
      const updated = apiResponse.data;

      // Update in current prompts list
      const index = currentPrompts.value.findIndex((p: PromptInDb) => p.uid === promptId);
      if (index !== -1) {
        currentPrompts.value[index] = updated;
      }

      return updated;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error updating prompt:', error_);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  // Delete a prompt from a collection
  async function deletePrompt(collectionId: string, promptId: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      await deletePromptFromPromptCollection({
        path: {
          prompt_collection_uid: collectionId,
          prompt_uid: promptId,
        },
      });

      currentPrompts.value = currentPrompts.value.filter((p: PromptInDb) => p.uid !== promptId);
      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error deleting prompt:', error_);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Fetch prompts for a collection (without updating current state)
  async function getCollectionPrompts(collectionId: string): Promise<PromptInDb[]> {
    // Don't update the global loading state for this operation
    // since it's used for displaying counts in cards
    try {
      const response = await getPromptCollectionPrompts({
        path: { prompt_collection_uid: collectionId },
      });
      if (!response.data) {
        throw new Error('Failed to fetch collection prompts');
      }
      return response.data;
    } catch (error_) {
      logger.error('Error fetching collection prompts:', error_);
      return [];
    }
  }

  // Add a new method to create a collection with AI
  async function createCollectionWithAI(data: {
    name: string;
    instructions: string;
    context: string;
  }): Promise<PromptCollectionInDb | null> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await createCollectionAi({
        body: {
          name: data.name,
          user_instructions: data.instructions,
          context: data.context,
          summary_prompt: '',
        },
      });

      if (!response.data) {
        throw new Error('Failed to create collection with AI');
      }
      const result = response.data;

      // Update collections list
      await fetchCollections();
      return result;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : String(error_);
      logger.error('Error creating collection with AI:', error_);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // State
    collections,
    totalCount,
    currentCollection,
    currentPrompts,
    isLoading,
    error,

    // Getters
    getCollection,

    // Actions
    fetchCollections,
    createCollection,
    deleteCollection,
    updateCollection,
    fetchCollectionWithPrompts,
    addPrompt,
    updatePrompt,
    deletePrompt,
    getCollectionPrompts,
    createCollectionWithAI,
  };
});
